'use client';

import { useState } from 'react';
import { useRouter } from 'next/navigation';
import { tw } from '@tw';
import { priceProblem } from '@peach/types/payments/price-limits.js';
import type { SubscriptionPlanDto } from '@peach/types/subscriptions/subscription.dto.js';
import {
  CALLS_PER_WEEK_OPTIONS,
  PLAN_MINUTES_OPTIONS,
  planName,
} from '@peach/types/subscriptions/types.js';
import { savePlans } from '@/app/actions/subscriptions';
import { callAction } from '@/lib/call-action';

interface Row {
  on: boolean;
  callMinutes: number;
  price: string;
}

/** A tier the creator has not priced yet starts from these; they only seed the fields. */
const SEED: Record<number, { callMinutes: number; price: number }> = {
  0: { callMinutes: 15, price: 10 },
  1: { callMinutes: 60, price: 160 },
  2: { callMinutes: 60, price: 300 },
  3: { callMinutes: 60, price: 420 },
};

interface PlansEditorProps {
  plans: SubscriptionPlanDto[];
  username: string;
  /** Settings' switch: the first save takes the page's packs off sale. */
  switching?: boolean;
  /** The page around it already names the section. */
  titled?: boolean;
}

/** The creator's tiers, one row each; Save replaces them all. */
export function PlansEditor({
  plans,
  username,
  switching = false,
  titled = true,
}: PlansEditorProps) {
  const router = useRouter();
  const [rows, setRows] = useState<Record<number, Row>>(() =>
    Object.fromEntries(
      CALLS_PER_WEEK_OPTIONS.map((calls) => {
        const live = plans.find((p) => p.callsPerWeek === calls);
        const seed = live ?? SEED[calls]!;
        return [
          calls,
          {
            on: switching ? calls <= 1 : !!live,
            callMinutes: seed.callMinutes,
            price: String(seed.price),
          },
        ];
      })
    )
  );
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  const set = (calls: number, patch: Partial<Row>) =>
    setRows((r) => ({ ...r, [calls]: { ...r[calls]!, ...patch } }));

  const save = async () => {
    const chosen = CALLS_PER_WEEK_OPTIONS.filter((calls) => rows[calls]!.on).map((calls) => ({
      callsPerWeek: calls,
      callMinutes: rows[calls]!.callMinutes,
      price: Math.round(Number(rows[calls]!.price)),
    }));
    if (chosen.length === 0) return setMessage('Keep at least one plan');
    const problem = chosen.map((p) => priceProblem(p.price)).find(Boolean);
    if (problem) return setMessage(problem);
    setBusy(true);
    setMessage(null);
    const result = await callAction(() => savePlans(chosen, username));
    setBusy(false);
    if (!result.success) return setMessage(result.error);
    setMessage('Saved');
    router.refresh();
  };

  return (
    <div className="rounded-[var(--sf-radius)] border border-[var(--sf-line)] bg-[var(--sf-card)] px-3.5 py-3">
      {titled && (
        <span className="sf-title text-title-2 font-extrabold text-[var(--sf-ink)]">
          {switching ? 'Sell subscriptions' : 'Your plans'}
        </span>
      )}
      <span className="sf-note mt-1 block">
        {switching
          ? 'Clients pay every 4 weeks. Your packs and open offers come off sale; anyone who bought one keeps their calls.'
          : 'Billed every 4 weeks. A new price applies to new subscribers; current ones keep theirs.'}
      </span>

      <div className="mt-2.5 flex flex-col gap-2">
        {CALLS_PER_WEEK_OPTIONS.map((calls) => {
          const row = rows[calls]!;
          return (
            <div
              key={calls}
              className={tw(
                'flex flex-wrap items-center gap-2 rounded-[10px] bg-[var(--sf-page)] px-3 py-2',
                !row.on && 'opacity-60'
              )}
            >
              <label className="flex min-w-0 flex-1 items-center gap-2">
                <input
                  type="checkbox"
                  checked={row.on}
                  onChange={(e) => set(calls, { on: e.target.checked })}
                />
                <span className="text-p-3 font-extrabold text-[var(--sf-ink)]">
                  {planName(calls)}
                </span>
              </label>
              <select
                aria-label={`${planName(calls)} call length`}
                value={row.callMinutes}
                disabled={!row.on}
                onChange={(e) => set(calls, { callMinutes: Number(e.target.value) })}
                className="rounded-[8px] border border-[var(--sf-line)] bg-[var(--sf-card)] px-2 py-1 text-[16px] text-[var(--sf-ink)] sm:text-p-3"
              >
                {PLAN_MINUTES_OPTIONS.map((m) => (
                  <option key={m} value={m}>
                    {m} min
                  </option>
                ))}
              </select>
              <label className="flex items-center gap-1 text-p-3 text-[var(--sf-mut)]">
                $
                <input
                  aria-label={`${planName(calls)} price every 4 weeks`}
                  inputMode="numeric"
                  value={row.price}
                  disabled={!row.on}
                  onChange={(e) => set(calls, { price: e.target.value.replace(/[^\d]/g, '') })}
                  className="w-20 rounded-[8px] border border-[var(--sf-line)] bg-[var(--sf-card)] px-2 py-1 text-[16px] text-[var(--sf-ink)] sm:text-p-3"
                />
              </label>
            </div>
          );
        })}
      </div>

      {message && <span className="sf-note mt-2 block">{message}</span>}

      <button
        type="button"
        disabled={busy}
        onClick={() => void save()}
        className="mm-btn sf-cta sf-cta-ink mt-2.5"
      >
        {busy ? 'Saving…' : switching ? 'Switch to subscriptions' : 'Save plans'}
      </button>
    </div>
  );
}
